import { Diagnostic, linter } from "@codemirror/lint"
import { AppNode } from "@features/builder/graph/types"
import { NodesMapper } from "@features/builder/helpers/nodesMapper"
import { getParentsList } from "@utils/node/getParentsList"
import { EXPRESSION_RESERVED_KEYWORDS } from "@utils/node/keywords"

// Token types for expression parsing
type Token =
  | { type: "node"; nodeName: string; start: number; end: number }
  | { type: "locRow"; nodeName: string; row: string; start: number; end: number; rowStart: number }
  | {
      type: "locCol"
      nodeName: string
      row: string
      col: string
      start: number
      end: number
      colStart: number
    }
  | { type: "function"; name: string; start: number; end: number }
  | { type: "string"; value: string; start: number; end: number }

/**
 * Tokenize the expression to prevent false positives
 * e.g., "with" inside {node with spaces} should not be flagged as a function
 */
export function tokenizeExpression(text: string): Token[] {
  const tokens: Token[] = []
  let pos = 0

  while (pos < text.length) {
    const remaining = text.slice(pos)

    // Match .loc['row']['col'] pattern first (most specific)
    const locColMatch = remaining.match(/^\{([^}]+)\}\.loc\['([^']+)'\]\['([^']+)'\]/)
    if (locColMatch && locColMatch[1] && locColMatch[2] && locColMatch[3]) {
      const nodeName = locColMatch[1]
      const row = locColMatch[2]
      const col = locColMatch[3]
      const colStart = pos + locColMatch[0].lastIndexOf(`'${col}'`)
      tokens.push({
        type: "locCol",
        nodeName,
        row,
        col,
        start: pos,
        end: pos + locColMatch[0].length,
        colStart,
      })
      pos += locColMatch[0].length
      continue
    }

    // Match .loc['row'] pattern (second most specific)
    const locRowMatch = remaining.match(/^\{([^}]+)\}\.loc\['([^']+)'\]/)
    if (locRowMatch && locRowMatch[1] && locRowMatch[2]) {
      const nodeName = locRowMatch[1]
      const row = locRowMatch[2]
      const rowStart = pos + locRowMatch[0].indexOf(`'${row}'`)
      tokens.push({
        type: "locRow",
        nodeName,
        row,
        start: pos,
        end: pos + locRowMatch[0].length,
        rowStart,
      })
      pos += locRowMatch[0].length
      continue
    }

    // Match node references {nodeName}
    const nodeMatch = remaining.match(/^\{([^}]+)\}/)
    if (nodeMatch && nodeMatch[1]) {
      tokens.push({
        type: "node",
        nodeName: nodeMatch[1],
        start: pos,
        end: pos + nodeMatch[0].length,
      })
      pos += nodeMatch[0].length
      continue
    }

    // Match string literals (to skip them in function detection)
    const stringMatch = remaining.match(/^'([^']*)'/)
    if (stringMatch && stringMatch[1] !== undefined) {
      tokens.push({
        type: "string",
        value: stringMatch[1],
        start: pos,
        end: pos + stringMatch[0].length,
      })
      pos += stringMatch[0].length
      continue
    }

    // Match function calls - only outside of braces/strings
    const funcMatch = remaining.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/)
    if (funcMatch && funcMatch[1]) {
      const funcName = funcMatch[1]
      tokens.push({
        type: "function",
        name: funcName,
        start: pos,
        end: pos + funcName.length,
      })
      pos += funcName.length
      continue
    }

    // Skip other characters
    pos++
  }

  return tokens
}

/**
 * Validate tokens and return diagnostics
 * Exported for testing
 */
export function validateTokens(text: string, node: AppNode, mapper: NodesMapper): Diagnostic[] {
  const parents = getParentsList(node)
  const labels = mapper.labelToIdMap
  const validFunctions = new Set<string>(EXPRESSION_RESERVED_KEYWORDS)
  const diagnostics: Diagnostic[] = []
  const tokens = tokenizeExpression(text)

  // Validate each token based on its type
  for (const token of tokens) {
    const nodeRef =
      "nodeName" in token ? mapper.nodesMap.get(mapper.labelToIdMap.get(token.nodeName) ?? "") : ""

    if ("nodeName" in token) {
      const nodeId = mapper.labelToIdMap.get(token.nodeName)
      if (nodeId && !parents.includes(nodeId)) {
        diagnostics.push({
          from: token.start,
          to: token.end,
          severity: "warning",
          message: `Node '${token.nodeName}' is not a parent`,
        })
      }
    }

    switch (token.type) {
      case "node":
        // Check if node is in graph
        if (!labels.has(token.nodeName)) {
          diagnostics.push({
            from: token.start,
            to: token.end,
            severity: "error",
            message: `Node '${token.nodeName}' not found`,
          })
        }
        break

      case "locRow":
        if (nodeRef && "originalAttribute" in nodeRef.data) {
          const properties = nodeRef.data.originalAttribute.properties
          const rows: string[] = "index" in properties ? properties.index : []

          if (!rows.includes(token.row)) {
            diagnostics.push({
              from: token.rowStart,
              to: token.rowStart + token.row.length + 2,
              severity: "error",
              message: `Row '${token.row}' not found in node '${token.nodeName}'`,
            })
          }
        }
        break

      case "locCol":
        if (nodeRef && "originalAttribute" in nodeRef.data) {
          const properties = nodeRef.data.originalAttribute.properties
          const cols: string[] = "columns" in properties ? properties.columns : []

          if (!cols.includes(token.col)) {
            diagnostics.push({
              from: token.colStart,
              to: token.colStart + token.col.length + 2,
              severity: "error",
              message: `Column '${token.col}' not found in node '${token.nodeName}'`,
            })
          }
        }
        break

      case "function":
        if (!validFunctions.has(token.name)) {
          diagnostics.push({
            from: token.start,
            to: token.end,
            severity: "error",
            message: `Function '${token.name}' not found`,
          })
        }
        break
    }
  }

  return diagnostics
}

export function expressionLinter(node: AppNode, mapper: NodesMapper) {
  return linter((view) => {
    const text = view.state.doc.toString()
    return validateTokens(text, node, mapper)
  })
}
